/**
 * useActivityMonitoring Hook
 *
 * Custom React hook for monitoring and retrieving user activities.
 *
 * @module useActivityMonitoring
 */

import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import {
  ActivityMonitoringService,
  ActivityData,
  ActivityFilters,
  ActivityStatistics
} from '../services/ActivityMonitoringService';

/**
 * Optional date range for initial statistics fetch (e.g. last 30 days).
 */
export interface ActivityStatsDateRange {
  start: Date;
  end: Date;
}

/**
 * Hook return type
 */
interface UseActivityMonitoringReturn {
  // Data
  activities: ActivityData[];
  statistics: ActivityStatistics | null;

  // Loading states
  isLoading: boolean;
  isStatisticsLoading: boolean;

  // Error states
  error: string | null;

  // Pagination
  hasMore: boolean;

  // Methods
  fetchActivities: (filters?: ActivityFilters, reset?: boolean) => Promise<void>;
  fetchStatistics: (startDate?: Date, endDate?: Date) => Promise<void>;
  refreshActivities: () => Promise<void>;
  clearError: () => void;
}

/**
 * useActivityMonitoring Hook
 *
 * @param initialFilters - Initial filters to apply
 * @param pageSize - Number of activities per page
 * @param initialStatsDateRange - Optional date range for initial stats fetch (e.g. last 30 days for "Total Activities")
 * @returns Activity monitoring state and methods
 */
export const useActivityMonitoring = (
  initialFilters?: ActivityFilters,
  pageSize: number = 50,
  initialStatsDateRange?: ActivityStatsDateRange
): UseActivityMonitoringReturn => {
  const { user } = useAuth();
  
  // State
  const [activities, setActivities] = useState<ActivityData[]>([]);
  const [statistics, setStatistics] = useState<ActivityStatistics | null>(null);
  const [hasMore, setHasMore] = useState(false);
  
  // Loading states
  const [isLoading, setIsLoading] = useState(false);
  const [isStatisticsLoading, setIsStatisticsLoading] = useState(false);
  
  // Error state
  const [error, setError] = useState<string | null>(null);
  
  // Current filters
  const [currentFilters, setCurrentFilters] = useState<ActivityFilters | undefined>(initialFilters);

  /**
   * Clear error state
   */
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  /**
   * Fetch activities with filters
   */
  const fetchActivities = useCallback(async (
    filters?: ActivityFilters,
    reset: boolean = false
  ) => {
    try {
      setIsLoading(true);
      setError(null);
      
      const activeFilters = filters || currentFilters;
      
      // Update current filters
      if (filters) setCurrentFilters(filters);
      
      const result = await ActivityMonitoringService.getActivities(
        activeFilters,
        pageSize
      );
      
      if (reset) {
        setActivities(result.activities);
      } else {
        setActivities(prev => [...prev, ...result.activities]);
      }
      
      setHasMore(result.hasMore);
    } catch (err) {
      console.error('Error fetching activities:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch activities');
    } finally {
      setIsLoading(false);
    }
  }, [currentFilters, pageSize]);

  /**
   * Fetch activity statistics
   */
  const fetchStatistics = useCallback(async (startDate?: Date, endDate?: Date) => {
    try {
      setIsStatisticsLoading(true);
      setError(null);
      
      const stats = await ActivityMonitoringService.getActivityStatistics(startDate, endDate);
      setStatistics(stats);
    } catch (err) {
      console.error('Error fetching statistics:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch statistics');
    } finally {
      setIsStatisticsLoading(false);
    }
  }, []);

  /**
   * Refresh activities list
   */
  const refreshActivities = useCallback(async () => {
    await fetchActivities(currentFilters, true);
  }, [fetchActivities, currentFilters]);

  // Load initial data
  useEffect(() => {
    fetchActivities(initialFilters, true);
    if (initialStatsDateRange) {
      fetchStatistics(initialStatsDateRange.start, initialStatsDateRange.end);
    } else {
      fetchStatistics();
    }
  }, []); // Only run on mount

  return {
    // Data
    activities,
    statistics,
    
    // Loading states
    isLoading,
    isStatisticsLoading,
    
    // Error states
    error,
    
    // Pagination
    hasMore,
    
    // Methods
    fetchActivities,
    fetchStatistics,
    refreshActivities,
    clearError
  };
};

